/**
 * Sobre mí - Página pública con contenido gestionado desde admin
 *
 * Usa SiteLayout (header/footer coherente con BaseTemplate)
 * El texto se carga desde /api/site-settings (campo sobre_mi)
 * Header banner con video Mux en loop - versiones distintas para mobile/desktop
 */

import { SiteLayout } from "@/components/SiteLayout";
import { useSEO } from "@/hooks/useSEO";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import MuxPlayer from "@mux/mux-player-react";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { Navigate } from "react-router-dom";

/** Desktop: formato 4:3 (1472x1080) - mejor para pantallas anchas */
const SOBRE_MI_BANNER_DESKTOP =
  "1eK6Dwj01CWkQ10043p2J2tC9Rzsq02sR4Ql5iERBeKI01s";
/** Mobile: formato 16:9 (1920x1072) - mejor para vertical */
const SOBRE_MI_BANNER_MOBILE =
  "yITEbarMKwVSMkT1qJJhG4QN3v3p8701vgb5iBgNDkYM";

const SobreMi = () => {
  useSEO("sobreMi");
  const { isBaseTemplate: isDarkShell } = useTemplateSelection();
  const { settings, isLoading } = useSiteSettings();
  const [videoError, setVideoError] = useState(false);

  const content = settings.sobreMi ?? "";
  const handleVideoError = useCallback(() => setVideoError(true), []);

  if (!isLoading && settings.showSobreMi === false) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <SiteLayout>
        <div className="flex-1 flex items-center justify-center py-20">
          <Loader2
            className={cn(
              "w-8 h-8 animate-spin",
              isDarkShell ? "text-white/50" : "text-black/50",
            )}
          />
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {/* Banner video - mismo layout que la página principal (banner img) pero con Mux */}
      <section
        id="sobre-mi-banner"
        className={cn(
          "relative w-full overflow-hidden min-h-[60vh] sm:min-h-[65vh] lg:min-h-[70vh]",
          isDarkShell ? "bg-black" : "bg-white",
        )}
      >
        {/* Video Mux: mobile (16:9) y desktop (4:3) - cada uno optimizado para su formato */}
        <div className={`absolute inset-0 min-w-full min-h-full overflow-hidden ${videoError ? "hidden" : ""}`}>
          {/* Perf: ambos <MuxPlayer> se montan aunque uno esté `md:hidden`.
              CSS display:none NO impide que el <video> haga fetch del manifest.
              `preload="metadata"` limita el costo del que no se ve a ~10–30 KB (manifest + segment0)
              en vez de varios MB. + capamos rendición a 720p para banner decorativo. */}
          <MuxPlayer
            playbackId={SOBRE_MI_BANNER_MOBILE}
            streamType="on-demand"
            autoPlay="muted"
            loop
            muted
            playsInline
            preload="metadata"
            maxResolution="720p"
            capRenditionToPlayerSize
            disableCookies
            disableTracking
            onError={handleVideoError}
            className="sobre-mi-banner-video md:hidden"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center center",
            }}
          />
          <MuxPlayer
            playbackId={SOBRE_MI_BANNER_DESKTOP}
            streamType="on-demand"
            autoPlay="muted"
            loop
            muted
            playsInline
            preload="metadata"
            maxResolution="1080p"
            capRenditionToPlayerSize
            disableCookies
            disableTracking
            onError={handleVideoError}
            className="sobre-mi-banner-video hidden md:block"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center center",
            }}
          />
        </div>

        {/* Capa de oscuridad sobre el video (solo cuando el video está visible) */}
        <div
          className={`absolute inset-0 bg-black/35 pointer-events-none z-10 transition-opacity duration-500 ${videoError ? "opacity-0" : "opacity-100"}`}
          aria-hidden
        />

        {/* Fallback gradient - igual que BaseTemplate, visible si falla el video */}
        <div
          className={`sobre-mi-banner-fallback absolute inset-0 transition-opacity duration-500 ${videoError ? "opacity-100" : "opacity-0"}`}
          style={{
            background: `
              linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%),
              radial-gradient(circle at 20% 50%, #ffecd2 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, #fcb69f 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, #a8edea 0%, transparent 50%)
            `,
            backgroundBlendMode: "overlay, normal, normal, normal",
          }}
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3Ccircle cx='53' cy='7' r='1'/%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3Ccircle cx='7' cy='53' r='1'/%3E%3Ccircle cx='53' cy='53' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: "60px 60px",
            }}
          />
        </div>

        {/* Grain texture - igual que BaseTemplate */}
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none z-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='4' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            backgroundSize: "200px 200px",
          }}
        />

        <style>{`
          #sobre-mi-banner .sobre-mi-banner-video {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            min-width: 100% !important;
            min-height: 100% !important;
            --media-object-fit: cover !important;
            --media-object-position: center center;
          }
          #sobre-mi-banner .sobre-mi-banner-video::part(media),
          #sobre-mi-banner .sobre-mi-banner-video video {
            object-fit: cover !important;
            object-position: center center !important;
            width: 100% !important;
            height: 100% !important;
            min-width: 100% !important;
            min-height: 100% !important;
          }
          #sobre-mi-banner .sobre-mi-banner-video::part(bottom) {
            display: none !important;
          }
        `}</style>
      </section>

      <div className="w-full max-w-2xl mx-auto px-4 py-8 sm:py-12 md:py-16">
        <header className="text-center mb-10 sm:mb-14">
          <h1
            className={cn(
              "text-2xl sm:text-3xl md:text-4xl font-display font-light tracking-tight uppercase",
              isDarkShell ? "text-white" : "text-black",
            )}
          >
            Sobre mí
          </h1>
        </header>

        <section className="text-center">
          <p
            className={cn(
              "font-body text-base sm:text-lg leading-relaxed",
              isDarkShell ? "text-white/80" : "text-black/80",
            )}
          >
            {content}
          </p>
        </section>

        <div className="text-center mt-12 sm:mt-16">
          <a
            href="/"
            className={cn(
              "font-body inline-block text-xs uppercase tracking-[0.2em] transition-colors font-light",
              isDarkShell
                ? "text-white/60 hover:text-white"
                : "text-black/60 hover:text-black",
            )}
          >
            Volver al inicio
          </a>
        </div>
      </div>
    </SiteLayout>
  );
};

export default SobreMi;
